import React, { SyntheticEvent, useCallback, useState } from 'react';

import { Helmet } from 'react-helmet';

import Add from '@mui/icons-material/Add';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { Box, Button, Container, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useListRewards } from '~/hooks/apis/agent/useListRewards';
import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { formatDateSlashes } from '~/services/TimeService';

import type { Reward } from '~/types/services/agentHub';

import { capitalise } from '~/utils/stringUtils';

import RewardDialog from './RewardDialog';
import { buildRewardConditionsSummary } from './rewardForm';

const DEFAULT_SIZE_PER_PAGE = 10;

function AgentRewards() {
  const queryFilters = useQuery();
  const [tabValue, setTabValue] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [rewardData, setRewardData] = useState<Reward | undefined>();
  const { push: setQueryString } = useAppNavigation();

  const page = parseInt(queryFilters.get('page') ?? '0', 10);

  const { data: { rows: rewardList = [], total = 0 } = {}, isFetching: isListLoading } = useListRewards({
    page: page.toString(),
    pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
    isActive: tabValue === 0 ? 'true' : 'false',
  });

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    queryFilters.set('page', '0');
    setQueryString({
      search: queryFilters.toString(),
    });
  };

  const onPageChange = useCallback(
    (nextPage: number) => {
      queryFilters.set('page', nextPage.toString());
      setQueryString({
        search: queryFilters.toString(),
      });
    },
    [queryFilters, setQueryString],
  );

  const openModal = (nextMode: 'create' | 'edit', reward?: Reward) => {
    setMode(nextMode);
    setRewardData(reward);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const sharedOpts: Partial<GridColDef> = {
    editable: false,
    sortable: true,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    flex: 2,
  };

  const columns: Array<GridColDef> = [
    {
      ...sharedOpts,
      field: 'name',
      headerName: 'Name',
      renderHeader: () => <Typography variant="button">Name</Typography>,
      flex: 1.5,
    },
    {
      ...sharedOpts,
      field: 'description',
      headerName: 'Description',
      renderHeader: () => <Typography variant="button">Description</Typography>,
      flex: 1.5,
    },
    {
      ...sharedOpts,
      field: 'rewardType',
      headerName: 'Type',
      renderHeader: () => <Typography variant="button">Type</Typography>,
      flex: 1,
      renderCell: (params) => capitalise(params.value as string),
    },
    {
      ...sharedOpts,
      field: 'targetAudience',
      headerName: 'Audience',
      renderHeader: () => <Typography variant="button">Audience</Typography>,
      flex: 1,
      renderCell: (params) => capitalise(params.value as string),
    },
    {
      ...sharedOpts,
      field: 'startDate',
      headerName: 'Start',
      renderHeader: () => <Typography variant="button">Start</Typography>,
      flex: 1,
      renderCell: (params) => formatDateSlashes(params.value),
    },
    {
      ...sharedOpts,
      field: 'endDate',
      headerName: 'End',
      renderHeader: () => <Typography variant="button">End</Typography>,
      flex: 1,
      renderCell: (params) => formatDateSlashes(params.value),
    },
    {
      ...sharedOpts,
      field: 'expiryDate',
      headerName: 'Expiry',
      renderHeader: () => <Typography variant="button">Expiry</Typography>,
      flex: 1,
      renderCell: (params) => formatDateSlashes(params.value),
    },
    {
      ...sharedOpts,
      field: 'conditions',
      headerName: 'Conditions',
      renderHeader: () => <Typography variant="button">Conditions</Typography>,
      renderCell: (params) => {
        const row = params.row as Reward;
        return buildRewardConditionsSummary(row.conditions, row.operator);
      },
    },
    {
      ...sharedOpts,
      field: 'commissionPercentage',
      headerName: 'Commission',
      renderHeader: () => <Typography variant="button">Commission</Typography>,
      flex: 1,
      renderCell: (params) => {
        const row = params.row as Reward;
        const percentage = row.commissions[0]?.commissionPercentage;
        return percentage != null ? `${percentage}%` : '-';
      },
    },
    {
      ...sharedOpts,
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderHeader: () => <Typography variant="button">Actions</Typography>,
      renderCell: (params) => (
        <Button
          sx={{ padding: 0, margin: 0, display: 'inline-block', minHeight: 0, minWidth: 0 }}
          onClick={() => openModal('edit', params.row)}
        >
          <ModeEditIcon sx={{ color: 'text.primary' }} />
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Agent Campaigns</title>
      </Helmet>
      <Stack pt={2} direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <PageSubheader title="Agent Campaigns" />
        {tabValue === 0 && (
          <Button startIcon={<Add />} variant="contained" onClick={() => openModal('create')}>
            Create reward
          </Button>
        )}
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="reward tabs">
          <Tab label="Active" />
          <Tab label="Inactive" />
        </Tabs>
      </Box>
      <Box sx={{ pt: 3 }}>
        <DataGrid
          loading={isListLoading}
          columns={columns}
          getRowId={(row) => row.id}
          rows={rewardList}
          autoHeight
          rowCount={total}
          paginationMode="server"
          paginationModel={{ page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page: nextPage }) => onPageChange(nextPage)}
          getRowHeight={() => 'auto'}
        />
      </Box>
      <RewardDialog
        key={isModalOpen ? `${mode}-${rewardData?.id ?? 'create'}` : 'closed'}
        mode={mode}
        isOpen={isModalOpen}
        onClose={closeModal}
        data={rewardData}
      />
    </Container>
  );
}

export default AgentRewards;
